/**
 * PROJECT-REVIEW-LAUNCHER.WIDGET
 * Round button of the owl, fixed at the bottom right of the project : opens the review of the project at any time
 */
import { motion } from "framer-motion"
import Tooltip from "@/components/tooltip"
import { useAnimationsDisabled } from "@/utils/animation-mode.utils"

interface Props {
  label: string //Tooltip
  errorsCount: number
  warningsCount: number
  onClick: Function
}

//Round badge at the top right of the button
function Badge(props: { text: string; color: string; top: number; right: number; size: number }) {
  return (
    <span
      className="flex text-sm"
      style={{
        position: "absolute",
        top: props.top,
        right: props.right,
        minWidth: props.size,
        height: props.size,
        padding: props.size >= 20 ? "0 5px" : "0 4px",
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        color: "white",
        background: props.color,
        boxShadow: "0 0 0 2px white"
      }}>
      {props.text}
    </span>
  )
}

//Errors and warnings together : two drops joined by a small bridge (the shapes are blurred then sharpened by the filter, the
//numbers are written on top so they stay crisp). A white outline layer under the colored one draws the ring.
function BadgeDrops(props: { errors: number; warnings: number }) {
  const errorsWidth: number = Math.max(20, String(props.errors).length * 9 + 12)
  const warningsWidth: number = Math.max(18, String(props.warnings).length * 9 + 8)
  //The orange drop keeps the same centre whatever the number of digits (the one of two digits)
  const warningsRight: number = 13 - warningsWidth / 2
  const drop = (width: number, height: number, top: number, right: number, background: string) => (
    <span style={{ position: "absolute", top, right, width, height, borderRadius: 999, background }} />
  )

  return (
    <span style={{ position: "absolute", top: 0, right: -18, width: 52, height: 40 }}>
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <filter id="idt-badge-drops" x="-40%" y="-40%" width="180%" height="180%" colorInterpolationFilters="sRGB">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2.6" result="blur" />
            <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 16 -6" />
          </filter>
        </defs>
      </svg>
      <span style={{ position: "absolute", inset: 0, filter: "url(#idt-badge-drops)" }}>
        {drop(errorsWidth + 4, 24, -2, 8, "white")}
        {drop(warningsWidth + 4, 22, 15, warningsRight - 2, "white")}
      </span>
      <span style={{ position: "absolute", inset: 0, filter: "url(#idt-badge-drops)" }}>
        {drop(errorsWidth, 20, 0, 10, "var(--idt-red)")}
        {drop(warningsWidth, 18, 17, warningsRight, "var(--idt-orange)")}
      </span>
      <span
        className="flex text-sm"
        style={{ position: "absolute", top: 0, right: 10, width: errorsWidth, height: 20, alignItems: "center", justifyContent: "center", fontWeight: 700, color: "white" }}>
        {props.errors}
      </span>
      {/* White like the other badges (a light shadow helps it on the orange) */}
      <span
        className="flex text-sm"
        style={{ position: "absolute", top: 17, right: warningsRight, width: warningsWidth, height: 18, alignItems: "center", justifyContent: "center", fontWeight: 700, color: "white", textShadow: "0 1px 1px rgba(0, 0, 0, 0.25)" }}>
        {props.warnings}
      </span>
    </span>
  )
}

export default function ProjectReviewLauncher(props: Props) {
  const isAnimationsDisabled = useAnimationsDisabled()

  //The main badge shows what is left : errors first, then warnings, a check when everything is ready
  const badgeColor: string = props.errorsCount > 0 ? "var(--idt-red)" : props.warningsCount > 0 ? "var(--idt-orange)" : "var(--idt-green)"
  const badgeText: string = props.errorsCount > 0 ? String(props.errorsCount) : props.warningsCount > 0 ? String(props.warningsCount) : "✓"

  return (
    <div style={{ position: "fixed", right: 24, bottom: 24, zIndex: 40 }}>
      <Tooltip text={props.label} position="left">
        <motion.button
          type="button"
          aria-label={props.label}
          initial={isAnimationsDisabled ? false : { scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          whileHover={isAnimationsDisabled ? undefined : { scale: 1.08 }}
          whileTap={isAnimationsDisabled ? undefined : { scale: 0.95 }}
          transition={{ type: "spring", stiffness: 320, damping: 16 }}
          onClick={() => props.onClick()}
          className="flex cursor-pointer"
          style={{
            position: "relative",
            width: 64,
            height: 64,
            borderRadius: "50%",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            background: "white",
            boxShadow: "0 6px 24px rgba(17, 28, 43, 0.22)",
            overflow: "visible"
          }}>
          <img src={require("@/assets/owl.png")} alt="" style={{ width: 40, height: "auto" }} />
          {props.errorsCount > 0 && props.warningsCount > 0 ? (
            <BadgeDrops errors={props.errorsCount} warnings={props.warningsCount} />
          ) : (
            <Badge text={badgeText} color={badgeColor} top={-10} right={-10} size={22} />
          )}
        </motion.button>
      </Tooltip>
    </div>
  )
}
